import { Product } from '@/types/product'
import { Domain } from '@/types/dashboard/domain'
import { z } from 'zod'

// A plan switch whose own withdrawal period still runs.
export const SwitchWithdrawal = z.object({
  adjustment_id: z.number().int(),
  from_product: z.string(),
  to_product: z.string(),
  switched_at: z.number().int(),
  // Last second of the switch's withdrawal period.
  withdrawable_until: z.number().int()
})

export type SwitchWithdrawal = z.infer<typeof SwitchWithdrawal>

export const Subscription = z.object({
  id: z.number().int(),
  started: z
    .number()
    .int()
    .min(-9_223_372_036_854_775_808)
    // eslint-disable-next-line no-loss-of-precision
    .max(9_223_372_036_854_775_807),
  active_until: z
    .number()
    .int()
    .min(-9_223_372_036_854_775_808)
    // eslint-disable-next-line no-loss-of-precision
    .max(9_223_372_036_854_775_807),
  status: z.enum(['active', 'canceled', 'suspended', 'superseded']),
  billing_freq: z.enum(['monthly', 'quarterly', 'half-annually', 'annually', 'free']),
  canceled: z
    .number()
    .int()
    .min(-9_223_372_036_854_775_808)
    // eslint-disable-next-line no-loss-of-precision
    .max(9_223_372_036_854_775_807)
    .optional(),
  product: Product,
  domain: Domain.optional(),
  used_features: z.record(z.string(), z.any()).optional(),
  // Kubernetes: the namespace the plan bills.
  namespace: z.string().nullish(),
  // NIS2 suspension fields
  suspended_at: z.number().int().optional(),
  suspension_reason: z.string().optional(),
  // Withdrawal (Widerrufsrecht) fields
  withdrawn_at: z.number().int().optional(),
  withdrawable_until: z.number().int().optional(),
  // When a withdrawal declaration reached us, set before or without
  // withdrawn_at, since an operator settles one the panel cannot carry out.
  withdrawal_declared_at: z.number().int().optional(),
  // When a cancelled subscription's contract ends: the end of the billing
  // month of a monthly plan, the notice date of one paid for several months.
  access_until: z.number().int().optional(),
  // Refund of the unused full months of a prepaid plan: estimated while it is
  // cancelled and running, then what was refunded and when.
  estimated_refund_cents: z.number().int().optional(),
  prepaid_refund_cents: z.number().int().optional(),
  prepaid_refunded_at: z.number().int().optional(),
  switch_withdrawals: z.array(SwitchWithdrawal).optional()
})

export type Subscription = z.infer<typeof Subscription>

// What cancelling a subscription now would do.
export const CancelPreview = z.object({
  effective_at: z.number().int(),
  prepaid_until: z.number().int().optional(),
  estimated_refund_cents: z.number().int().optional(),
  // A monthly plan: the end of the billed period covering now, which a mail
  // plan switched off ends with.
  period_end: z.number().int().optional()
})

export type CancelPreview = z.infer<typeof CancelPreview>

// What withdrawing a subscription now would refund, gross as charged. Absent
// when the backend could not work it out.
export const WithdrawPreview = z.object({
  refund_cents: z.number().int().optional()
})

export type WithdrawPreview = z.infer<typeof WithdrawPreview>

// What withdrawing a plan switch now would do. automatic: the previous tier
// is restored at once; otherwise an operator restores it.
export const SwitchWithdrawPreview = z.object({
  automatic: z.boolean(),
  from_product: z.string(),
  to_product: z.string(),
  // Gross, after the set-off of wertersatz_cents; absent while the switch's
  // charge is still being collected.
  refund_cents: z.number().int().optional(),
  // Free tier: what the payments since the switch leave owed, invoiced on its own.
  owed_cents: z.number().int().optional(),
  // Gross, what the switch charged; 0 for a switch from the free tier.
  charged_cents: z.number().int().optional(),
  // What the time on the new plan up to now costs, set off against the refund;
  // for a free-to-PAYG switch (usage_amount_cents, an estimate) against what was
  // paid since the switch, with an uncovered rest invoiced on its own.
  wertersatz_cents: z.number().int().optional(),
  usage_amount_cents: z.number().int().optional(),
  // Kubernetes: the namespace uses more than the restored tier includes.
  over_quota: z.boolean().optional(),
  storage_used_bytes: z.number().int().optional(),
  storage_included_bytes: z.number().int().optional(),
  // Per GB and month, on the account's basis, a fraction of a cent.
  overage_price_cents: z.number().optional(),
  // Monthly Kubernetes upgrade: what restoring the previous tier charges for
  // the rest of the month, its monthly price, and the month's exclusive end.
  old_amount_cents: z.number().int().optional(),
  old_price_cents: z.number().int().optional(),
  month_end: z.string().optional()
})

export type SwitchWithdrawPreview = z.infer<typeof SwitchWithdrawPreview>

// The variants of the withdrawal receipt the backend mails.
export const SwitchReceiptKind = z.enum([
  'refund',
  'nothing_charged',
  'nothing_charged_pending',
  'paid_pending',
  'paid_reversed',
  'wertersatz',
  'free_set_off',
  'free_exact_cover',
  'free_part_set_off',
  'free_invoiced',
  'free_pending',
  'older',
  'older_wertersatz',
  'by_hand',
  'by_hand_wertersatz',
  'by_hand_charge_later',
  'by_hand_free'
])

export type SwitchReceiptKind = z.infer<typeof SwitchReceiptKind>

export const SwitchWithdrawResult = z.object({
  executed: z.boolean(),
  refund_amount: z.number().int().optional(),
  // As in the preview, worked out at the declaration.
  refund_cents: z.number().int().optional(),
  charged_cents: z.number().int().optional(),
  wertersatz_cents: z.number().int().optional(),
  usage_amount_cents: z.number().int().optional(),
  // The free-tier set-off receipts' parts of wertersatz_cents, and the
  // payments since the switch they set it off against.
  base_fee_wertersatz_cents: z.number().int().optional(),
  usage_wertersatz_cents: z.number().int().optional(),
  paid_cents: z.number().int().optional(),
  // The receipt mailed; null when the backend could not tell, and a variant
  // this build does not know reads as none.
  receipt_kind: SwitchReceiptKind.nullish().catch(undefined),
  // What a switch from the free tier invoices on its own.
  owed_cents: z.number().int().nullish(),
  // When the switch that followed an older one took effect.
  next_switch_at: z.string().datetime({ offset: true }).nullish(),
  message: z.string().optional()
})

export type SwitchWithdrawResult = z.infer<typeof SwitchWithdrawResult>
